import { createContext, useContext, useState, ReactNode, Context, lazy, Suspense } from "react";

// Lazy-load the dialog so Supabase + react-hook-form stay out of the initial bundle.
const DemoRequestDialog = lazy(() =>
  import("./DemoRequestDialog")
    .then((m) => ({ default: m.DemoRequestDialog }))
    .catch((error) => {
      console.error("Failed to load DemoRequestDialog", error);
      return { default: () => null };
    }),
);

interface DemoDialogContextType {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

// Cache context on globalThis so React Fast Refresh doesn't break the provider/consumer pairing.
const globalKey = "__ibylit_demo_dialog_context__";
const DemoDialogContext: Context<DemoDialogContextType | undefined> =
  (globalThis as any)[globalKey] ??
  ((globalThis as any)[globalKey] = createContext<DemoDialogContextType | undefined>(undefined));
DemoDialogContext.displayName = "DemoDialogContext";

export const DemoRequestProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const open = () => {
    setHasOpened(true);
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);

  return (
    <DemoDialogContext.Provider value={{ open, close, isOpen }}>
      {children}
      {hasOpened && (
        <Suspense fallback={null}>
          <DemoRequestDialog open={isOpen} onOpenChange={setIsOpen} />
        </Suspense>
      )}
    </DemoDialogContext.Provider>
  );
};

export const useDemoDialog = () => {
  const ctx = useContext(DemoDialogContext);
  if (!ctx) {
    // Safe fallback: if the provider is missing, no-op so the page doesn't crash.
    return { open: () => {}, close: () => {}, isOpen: false };
  }
  return ctx;
};
